import type { LocalizedString } from './types';

/**
 * `whatIsDescription` copy for every music service. Defined with a local
 * `defineMessages` identity so `formatjs extract` can still collect ids and
 * `defaultMessage` bodies via AST pattern matching, while keeping fym-shared
 * free of a runtime `react-intl` import. Pulling `react-intl` at module load
 * broke Next.js server routes (e.g. `app/sitemap.ts`) whose RSC bundle cannot
 * evaluate `React.createContext` at build time.
 *
 * IDs are stable: `fym-shared.musicService.<camelCaseServiceId>.whatIs`.
 */
function defineMessages<T extends Record<string, LocalizedString>>(messages: T): T {
  return messages;
}

export const musicServiceMessages = defineMessages({
  amazonMusicWhatIs: {
    id: 'fym-shared.musicService.amazonMusic.whatIs',
    defaultMessage:
      'Amazon Music has a 100 million song music library, offers personalized playlists, and is integrated with Alexa. You’ll find all the most popular songs and albums. Free and premium plans are available.',
  },
  appleMusicWhatIs: {
    id: 'fym-shared.musicService.appleMusic.whatIs',
    defaultMessage:
      'Apple Music is a streaming service with over 100 million songs, curated playlists, and exclusive content. With a premium subscription, you can enjoy seamless integration with Apple devices and high-quality audio.',
  },
  audiomackWhatIs: {
    id: 'fym-shared.musicService.audiomack.whatIs',
    defaultMessage:
      'Audiomack is a completely free platform for content creators to share their music without limitations or restrictions. It focuses exclusively on new, independent, and up-and-coming artists. They are not a full-catalog streaming service like Spotify and Apple Music, and do not plan to ever become one.',
  },
  audiusWhatIs: {
    id: 'fym-shared.musicService.audius.whatIs',
    defaultMessage:
      'Audius is a music streaming platform built on blockchain technology, offering millions of tracks from independent artists around the world. You can use Audius on your mobile, browser, or desktop app. It’s free to sign up, stream, and share music.',
  },
  boomplayWhatIs: {
    id: 'fym-shared.musicService.boomplay.whatIs',
    defaultMessage:
      'Boomplay is a music streaming service popular in Africa, offering over 90 million songs, music videos, and podcasts. Explore local and international hits with free and premium plans.',
  },
  deezerWhatIs: {
    id: 'fym-shared.musicService.deezer.whatIs',
    defaultMessage:
      'Deezer is a music streaming service with over 90 million songs, podcasts, and radio channels. With a premium subscription, you can enjoy personalized recommendations, curated playlists, and high-fidelity lossless audio.',
  },
  gaanaWhatIs: {
    id: 'fym-shared.musicService.gaana.whatIs',
    defaultMessage:
      'Gaana is a popular music streaming service in India. It offers over 45 million tracks of Bollywood, regional Indian music, and international hits. Listen on mobile devices and the web with free and premium plans.',
  },
  jiosaavnWhatIs: {
    id: 'fym-shared.musicService.jiosaavn.whatIs',
    defaultMessage:
      'JioSaavn is one of India’s biggest music streaming services, with over 43 million songs. It offers a vast library of Bollywood, regional Indian music, and international hits. You can enjoy exclusive content, podcasts, and high-quality audio with a premium subscription.',
  },
  napsterWhatIs: {
    id: 'fym-shared.musicService.napster.whatIs',
    defaultMessage:
      'Napster, the music streaming pioneer, offers over 100 million songs, curated playlists, and high-quality audio with a premium subscription. Enjoy a focus on hip-hop and electronic music genres.',
  },
  pandoraWhatIs: {
    id: 'fym-shared.musicService.pandora.whatIs',
    defaultMessage:
      'Pandora is a music streaming service with a personalized radio-style experience and a 30 million-strong catalog. Create stations based on your favorite artists or songs and discover new music through recommendations. Free and premium plans are available.',
  },
  qobuzWhatIs: {
    id: 'fym-shared.musicService.qobuz.whatIs',
    defaultMessage:
      'Qobuz is a music streaming service focused on high-fidelity and lossless audio quality. Explore an expertly curated library of 100 million tracks and discover new music with expert recommendations.',
  },
  qqMusicWhatIs: {
    id: 'fym-shared.musicService.qqMusic.whatIs',
    defaultMessage:
      'QQ Music is a popular music streaming service in China. It offers millions of songs, music videos, and karaoke features. With a premium subscription, you can enjoy personalized recommendations, social features, and high-quality audio.',
  },
  soundcloudWhatIs: {
    id: 'fym-shared.musicService.soundcloud.whatIs',
    defaultMessage:
      'SoundCloud is a music streaming platform focusing on independent artists and music discovery. With 135+ million tracks, you can upload, share, and listen to a massive music library, remixes, and podcasts. Free and premium plans are available.',
  },
  spotifyWhatIs: {
    id: 'fym-shared.musicService.spotify.whatIs',
    defaultMessage:
      'Spotify is a music streaming service that offers over 100 million songs, thousands of podcasts, and audiobooks. You can use Spotify on your mobile, browser, or desktop app. You can create an account for free.',
  },
  tidalWhatIs: {
    id: 'fym-shared.musicService.tidal.whatIs',
    defaultMessage:
      'TIDAL is a music streaming service focused on high-fidelity and lossless audio quality and hosts over 100 million tracks. Explore millions of songs, curated playlists, and exclusive content from artists with a premium subscription. Enjoy high-quality audio and support artist payouts with a premium subscription.',
  },
  vkMusicWhatIs: {
    id: 'fym-shared.musicService.vkMusic.whatIs',
    defaultMessage:
      'VK Music is a streaming service integrated with the popular Russian social network VK. Users can listen to millions of songs, create playlists, and share music with friends. Free and premium plans are available.',
  },
  yandexMusicWhatIs: {
    id: 'fym-shared.musicService.yandexMusic.whatIs',
    defaultMessage:
      'Yandex Music is a popular music streaming service in Russia. It offers millions of songs, curated playlists, and integration with other Yandex services. With a premium subscription, you can enjoy high-quality audio and personalized recommendations.',
  },
  youtubeWhatIs: {
    id: 'fym-shared.musicService.youtube.whatIs',
    defaultMessage:
      "YouTube isn't strictly a music streaming service but offers a vast library of music videos and songs you can’t find anywhere else. You can create playlists, listen for free, and enjoy recommended content based on your preferences.",
  },
  youtubeMusicWhatIs: {
    id: 'fym-shared.musicService.youtubeMusic.whatIs',
    defaultMessage:
      'YouTube Music is a streaming service with access to over 100 million songs, music videos, and remixes. A premium subscription allows you to explore curated playlists, personalized recommendations, and background listening.',
  },
  zvukWhatIs: {
    id: 'fym-shared.musicService.zvuk.whatIs',
    defaultMessage:
      'Zvuk is a music streaming service popular in Russia and the CIS countries. It offers millions of songs, podcasts, and audiobooks. With a premium subscription, you can enjoy personalized recommendations, curated playlists, and high-quality audio.',
  },
});
